import { useCallback, useEffect, useState } from "react";

export type AppPerspective = "admin" | "teacher" | "student";

const PERSPECTIVE_STORAGE_PREFIX = "addvance-role-perspective";
const PERSPECTIVE_CHANGE_EVENT = "addvance-role-perspective-change";

const isAppPerspective = (value: unknown): value is AppPerspective => {
  return value === "admin" || value === "teacher" || value === "student";
};

const getStorageKey = (userId?: string | null) => {
  return userId ? `${PERSPECTIVE_STORAGE_PREFIX}:${userId}` : null;
};

const getStoredPerspective = (userId?: string | null) => {
  if (typeof window === "undefined") {
    return null;
  }

  const storageKey = getStorageKey(userId);
  if (!storageKey) {
    return null;
  }

  try {
    const stored = localStorage.getItem(storageKey);
    return isAppPerspective(stored) ? stored : null;
  } catch {
    return null;
  }
};

export function useRolePerspective(userId?: string | null, actualRole?: string | null) {
  const isAdminAccount = actualRole === "admin";
  const storageKey = getStorageKey(userId);

  const [perspective, setPerspectiveState] = useState<AppPerspective | null>(() => {
    if (!isAdminAccount) {
      return null;
    }

    return getStoredPerspective(userId) ?? "admin";
  });

  useEffect(() => {
    if (!isAdminAccount) {
      setPerspectiveState(null);
      return;
    }

    setPerspectiveState(getStoredPerspective(userId) ?? "admin");
  }, [actualRole, isAdminAccount, userId]);

  useEffect(() => {
    if (typeof window === "undefined" || !isAdminAccount || !storageKey) {
      return;
    }

    const refreshPerspective = () => {
      setPerspectiveState(getStoredPerspective(userId) ?? "admin");
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        refreshPerspective();
      }
    };

    const handlePerspectiveChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ userId?: string }>;
      if (customEvent.detail?.userId && customEvent.detail.userId !== userId) {
        return;
      }

      refreshPerspective();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(PERSPECTIVE_CHANGE_EVENT, handlePerspectiveChange as EventListener);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(PERSPECTIVE_CHANGE_EVENT, handlePerspectiveChange as EventListener);
    };
  }, [isAdminAccount, storageKey, userId]);

  const setPerspective = useCallback((nextPerspective: AppPerspective) => {
    if (typeof window === "undefined" || !isAdminAccount || !storageKey) {
      return;
    }

    try {
      localStorage.setItem(storageKey, nextPerspective);
    } catch {
      // Ignore storage failures and keep the in-memory state in sync.
    }

    setPerspectiveState(nextPerspective);
    window.dispatchEvent(new CustomEvent(PERSPECTIVE_CHANGE_EVENT, { detail: { userId } }));
  }, [isAdminAccount, storageKey, userId]);

  const effectiveRole = isAdminAccount ? perspective ?? "admin" : actualRole ?? null;

  return {
    perspective: isAdminAccount ? perspective ?? "admin" : null,
    setPerspective,
    effectiveRole,
    isAdminAccount,
  };
}